import { useState, type ReactElement } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mag, type Recht } from '@vip/shared';

import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '../auth/useAuth';
import { VocabulairePagina } from '../vocabulaire/VocabulairePagina';
import { AccountPaneel } from './AccountPaneel';
import { BegeleidingPaneel, BewarenPaneel } from './GetallenPanelen';
import { MailPaneel } from './MailPaneel';
import { RechtenPaneel } from './RechtenPaneel';
import { VerwerkingPaneel } from './VerwerkingPaneel';

/**
 * Instellingen staat buiten `Layout`, dus zonder het hoofdmenu.
 *
 * Dat is bewust: het beheerscherm was uitgegroeid tot negen secties onder
 * elkaar, en die horen niet naast "Dashboard" in dezelfde navigatie te staan.
 * Je stapt hier even uit de app; de balk bovenaan brengt je terug.
 *
 * Welke tabjes je ziet volgt uit de rechten, niet uit je rol. Een buddy komt
 * hier voor zijn wachtwoord en ziet verder niets.
 */
interface Paneel {
  id: string;
  label: string;
  recht?: Recht;
  inhoud: () => ReactElement;
}

const PANELEN: Paneel[] = [
  { id: 'vocabulaire', label: 'Vaktermen', recht: 'vocabulary:manage', inhoud: VocabulairePagina },
  { id: 'verwerking', label: 'Verwerking', recht: 'settings:write', inhoud: VerwerkingPaneel },
  { id: 'bewaren', label: 'Bewaren', recht: 'settings:write', inhoud: BewarenPaneel },
  {
    id: 'begeleiding',
    label: 'Begeleiding',
    recht: 'settings:write',
    inhoud: BegeleidingPaneel,
  },
  { id: 'mail', label: 'Mail', recht: 'settings:write', inhoud: MailPaneel },
  // Naslag en geen knop: wie de vinkjes zet, kan hier opzoeken wat ze opleveren.
  { id: 'rechten', label: 'Rechten', recht: 'persoon:manage', inhoud: RechtenPaneel },
  { id: 'account', label: 'Mijn account', inhoud: AccountPaneel },
];

/** Zelfde breedte als het logo op de andere schermen, zodat de terugknop op één
 *  lijn staat met waar je vandaan komt. */
const BREEDTE = 'mx-auto w-full max-w-7xl px-4 md:px-8';

export function InstellingenPagina() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const zichtbaar = PANELEN.filter((p) => !p.recht || mag(user?.rechten, p.recht));
  const [actief, setActief] = useState(zichtbaar[0]?.id ?? 'account');

  return (
    <div className="min-h-svh bg-background">
      {/* Plakkend, want de tabjes hieronder kunnen je ver naar beneden brengen
          en dan moet de uitgang in beeld blijven. */}
      <div className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur">
        <div className={`${BREEDTE} py-3`}>
          <Button onClick={() => navigate('/')} size="sm" variant="ghost">
            <ArrowLeftIcon data-icon="inline-start" />
            Terug
          </Button>
        </div>
      </div>

      <div className={`${BREEDTE} flex flex-col gap-6 pt-8 pb-16`}>
        <h1 className="font-heading text-2xl font-semibold">Instellingen</h1>

        <Tabs onValueChange={setActief} value={actief}>
          {zichtbaar.length > 1 && (
            <TabsList>
              {zichtbaar.map((p) => (
                <TabsTrigger key={p.id} value={p.id}>
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}

          {/* `TabsContent` hangt de inhoud uit de boom zolang een tabje niet
              actief is, dus de uitvragen van een paneel lopen pas als je het
              opent. Dat was met het oude ene-paneel-tegelijk ook zo. */}
          {zichtbaar.map((p) => (
            <TabsContent className="flex flex-col gap-6" key={p.id} value={p.id}>
              <p.inhoud />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
